import { Button } from '@crowdex/ui/components/button';
import {
  DesktopDropdownContent,
  DesktopDropdownItem,
  DesktopDropdownLabel,
  DesktopDropdownRadioItem,
} from '@crowdex/ui/components/desktop';
import {
  DropdownMenu,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import { Check, ChevronDown, GitBranch, Laptop, LoaderCircle, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useComposerMenuSide } from '@/components/desktop-shell/composer-menu-side';
import { type BranchScope, pickerBranches } from '@/lib/desktop-shell/branches';
import { type CloudHandoffSource, cloudHandoffScope } from '@/lib/desktop-shell/cloud-handoff';
import type { DesktopPlatform } from '@/lib/electron-bridge';
import type { BranchTarget, NativeGitStatus } from '@/lib/native-desktop';
import { userPlatform } from '@/lib/platform';
export type ExecutionMode = 'cloud' | 'local' | 'worktree';
export type RemoteExecutionTarget = {
  /** Whether the provider runs on Crowdex Cloud rather than on the session's server. */
  cloud: boolean;
  providerId: string;
  profileId: string;
  label: string;
  description?: string;
};

/**
 * How a setup control's trigger looks: inline text (the summary), a chip of the new
 * conversation's composer, or a field of "Where it runs".
 */
export type SetupControlVariant = 'inline' | 'chip' | 'field';

/** A chip of the new conversation's composer (project, repository, branch, agent). */
export const SETUP_CHIP_CLASS =
  'flex h-8 min-w-0 max-w-56 shrink-0 items-center gap-1.5 rounded-control bg-control px-3 type-meta text-foreground transition-colors hover:bg-control/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 data-[state=open]:bg-control/75';

/** A field of "Where it runs" (start from, agent). */
const SETUP_FIELD_CLASS =
  'flex h-11 w-full min-w-0 items-center gap-2 rounded-xl bg-control px-3 text-left text-sm text-foreground transition-colors hover:bg-control/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/** The trigger of a branch control in a chip or a field: its icon, its label, a chevron. */
function SetupBranchTrigger({
  variant,
  label,
  pending = false,
  disabled,
  title,
  ...props
}: {
  variant: 'chip' | 'field';
  label: string;
  pending?: boolean;
  disabled: boolean;
  title: string;
}) {
  return (
    <button
      aria-label={variant === 'chip' ? 'Branch' : 'Start from'}
      className={variant === 'chip' ? SETUP_CHIP_CLASS : SETUP_FIELD_CLASS}
      disabled={disabled}
      title={title}
      type="button"
      {...props}
    >
      {variant === 'chip' &&
        (pending ? (
          <LoaderCircle className="size-3.5 shrink-0 animate-spin" />
        ) : (
          <GitBranch className="size-3.5 shrink-0 text-muted-foreground" />
        ))}
      <span className={cn('min-w-0 truncate', variant === 'field' && 'flex-1 font-mono')}>
        {label}
      </span>
      <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
    </button>
  );
}

export type WorktreeStartingState =
  | { type: 'working-tree' }
  | { type: 'branch'; branch: string; remote: boolean };

/** The local machine's name for the user's platform (`null`: the platform this UI runs on). */
export function localMachineLabel(platform: DesktopPlatform | null): string {
  const current = platform ?? userPlatform();
  if (current === 'macos') return 'This Mac';
  if (current === 'windows') return 'This PC';
  return 'This computer';
}

/**
 * The shared local | remote branch selector: a segmented scope toggle, a search
 * box, and a radio list of branches with pinned/current/default emphasis and
 * per-scope empty states. The trigger, the active scope, the highlighted
 * selection, and what a pick does are all supplied by the caller so the same
 * surface backs both the cloud-handoff source picker and the worktree branch
 * switcher.
 */
function BranchPicker({
  currentBranch,
  defaultBranch,
  footer,
  localBranches,
  onOpen,
  onScopeChange,
  onSelectBranch,
  remoteBranches,
  scope,
  scopes = ['local', 'remote'],
  selectedValue,
  trigger,
}: {
  currentBranch: string;
  defaultBranch: string | null;
  footer?: ReactNode;
  localBranches: readonly string[];
  onOpen?: () => void;
  onScopeChange: (scope: BranchScope) => void;
  onSelectBranch: (scope: BranchScope, branch: string) => void;
  remoteBranches: readonly string[];
  scope: BranchScope;
  scopes?: readonly BranchScope[];
  selectedValue: string;
  trigger: ReactNode;
}) {
  const menuSide = useComposerMenuSide();
  const [search, setSearch] = useState('');
  const listed = pickerBranches(
    scope,
    { localBranches, remoteBranches, currentBranch, defaultBranch },
    search.trim(),
  );

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) onOpen?.();
        else setSearch('');
      }}
    >
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DesktopDropdownContent align="start" className="w-72 overflow-hidden p-0" side={menuSide}>
        <div className="border-desktop-hairline space-y-1.5 border-b px-2 py-2">
          {scopes.length > 1 && (
            <div className="grid grid-cols-2 gap-0.5 rounded-lg bg-muted/70 p-0.5">
              {scopes.map((option) => (
                <button
                  className={cn(
                    'h-control-xs rounded-md font-medium text-caption transition-colors',
                    scope === option
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  key={option}
                  onClick={() => onScopeChange(option)}
                  type="button"
                >
                  {option === 'local' ? 'Local' : 'Remote'}
                </button>
              ))}
            </div>
          )}
          <Input
            aria-label="Search branches"
            appearance="bare"
            className="h-control-compact px-1.5 text-sm placeholder:text-muted-foreground"
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder="Search branches…"
            value={search}
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          <DropdownMenuRadioGroup
            onValueChange={(value) => onSelectBranch(scope, value.slice(value.indexOf(':') + 1))}
            value={selectedValue}
          >
            {listed.map((branch) => (
              <DesktopDropdownRadioItem key={`${scope}:${branch}`} value={`${scope}:${branch}`}>
                <span className="min-w-0 flex-1 truncate">{branch}</span>
                {(branch === defaultBranch || branch === currentBranch) && (
                  <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground text-xs">
                    {branch === defaultBranch && <span>default</span>}
                    {branch === currentBranch && <span>current</span>}
                  </span>
                )}
              </DesktopDropdownRadioItem>
            ))}
            {listed.length === 0 && (
              <p className="px-3 py-4 text-center text-muted-foreground text-xs">
                {scope === 'remote' ? 'No remote branches found' : 'No local branches found'}
              </p>
            )}
          </DropdownMenuRadioGroup>
        </div>
        {footer}
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

export function CloudHandoffSourceMenu({
  currentBranch,
  defaultBranch,
  disabled,
  localBranches,
  onOpen,
  onSourceChange,
  remoteBranches,
  source,
  variant = 'inline',
}: {
  currentBranch: string;
  defaultBranch: string | null;
  disabled: boolean;
  localBranches: readonly string[];
  onOpen?: () => void;
  onSourceChange: (source: CloudHandoffSource) => void;
  remoteBranches: readonly string[];
  source: CloudHandoffSource | null;
  variant?: SetupControlVariant;
}) {
  const scope = cloudHandoffScope(source);
  const workspaceLabel = currentBranch.trim() || defaultBranch || 'Current workspace';
  const label =
    source?.type === 'remote' || source?.type === 'local' ? source.branch : workspaceLabel;

  function selectScope(next: BranchScope): void {
    if (next === 'local') {
      onSourceChange(
        currentBranch.trim().length > 0
          ? { type: 'local', branch: currentBranch }
          : { type: 'workspace' },
      );
      return;
    }
    const branch = defaultBranch ?? remoteBranches[0] ?? currentBranch.trim();
    if (branch.length === 0) return;
    onSourceChange({ type: 'remote', branch });
    onOpen?.();
  }

  const selectedValue =
    source?.type === 'remote'
      ? `remote:${source.branch}`
      : source?.type === 'local'
        ? `local:${source.branch}`
        : `local:${currentBranch}`;

  return (
    <BranchPicker
      currentBranch={currentBranch}
      defaultBranch={defaultBranch}
      localBranches={localBranches}
      onOpen={onOpen}
      onScopeChange={selectScope}
      onSelectBranch={(nextScope, branch) =>
        onSourceChange(
          nextScope === 'remote' ? { type: 'remote', branch } : { type: 'local', branch },
        )
      }
      remoteBranches={remoteBranches}
      scope={scope}
      selectedValue={selectedValue}
      trigger={
        variant === 'inline' ? (
          <button
            className="flex min-w-0 items-center gap-2 hover:text-foreground disabled:opacity-50"
            disabled={disabled}
            title="Choose a local or remote branch"
            type="button"
          >
            <GitBranch className="size-4 shrink-0" />
            <span className="max-w-56 truncate">{label}</span>
            <ChevronDown className="size-3.5 shrink-0" />
          </button>
        ) : (
          <SetupBranchTrigger
            disabled={disabled}
            label={label}
            title="Choose a local or remote branch"
            variant={variant}
          />
        )
      }
    />
  );
}

export function WorktreeStartingPointMenu({
  disabled,
  gitStatus,
  onChange,
  onOpen,
  value,
  variant = 'inline',
}: {
  disabled: boolean;
  gitStatus: NativeGitStatus | null;
  onChange: (state: WorktreeStartingState) => void;
  /** Refreshes the remote branches when the menu opens. */
  onOpen?: () => void;
  value: WorktreeStartingState;
  variant?: SetupControlVariant;
}) {
  const menuSide = useComposerMenuSide();
  const [search, setSearch] = useState('');
  if (gitStatus === null) return null;
  const query = search.trim().toLowerCase();
  const branches = gitStatus.branches.filter((branch) => branch.toLowerCase().includes(query));
  const remoteBranches = gitStatus.remoteBranches.filter((branch) =>
    branch.toLowerCase().includes(query),
  );
  const currentBranch = gitStatus.branch || 'Detached HEAD';
  const label =
    value.type === 'working-tree'
      ? `${currentBranch} (current)`
      : value.remote
        ? `origin/${value.branch}`
        : value.branch;
  const hasChanges = gitStatus.additions > 0 || gitStatus.deletions > 0;
  const selected = (branch: string, remote: boolean) =>
    value.type === 'branch' && value.branch === branch && value.remote === remote;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) onOpen?.();
        else setSearch('');
      }}
    >
      <DropdownMenuTrigger asChild>
        {variant === 'inline' ? (
          <Button
            className="h-control-compact max-w-44 gap-1.5 px-2 text-xs"
            disabled={disabled}
            title="Choose the worktree starting point"
            variant="ghost"
          >
            <GitBranch className="size-3.5 shrink-0" />
            <span className="truncate">{label}</span>
            <ChevronDown className="size-3 shrink-0" />
          </Button>
        ) : (
          <SetupBranchTrigger
            disabled={disabled}
            label={label}
            title="Choose the worktree starting point"
            variant={variant}
          />
        )}
      </DropdownMenuTrigger>
      <DesktopDropdownContent align="start" className="w-72 overflow-hidden p-0" side={menuSide}>
        <div className="border-desktop-hairline border-b px-3 py-1.5">
          <Input
            aria-label="Search repository branches"
            appearance="bare"
            className="h-control-compact px-0 text-sm placeholder:text-muted-foreground"
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder="Search branches…"
            value={search}
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {query.length === 0 && (
            <>
              <DesktopDropdownLabel>Local file state</DesktopDropdownLabel>
              <DesktopDropdownItem onSelect={() => onChange({ type: 'working-tree' })}>
                <Laptop className="size-4" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{currentBranch}</span>
                  {hasChanges && (
                    <span className="block text-muted-foreground text-xs">
                      with local code changes
                    </span>
                  )}
                </span>
                {value.type === 'working-tree' && <Check className="size-4" />}
              </DesktopDropdownItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DesktopDropdownLabel>Local branches</DesktopDropdownLabel>
          {branches.map((branch) => (
            <DesktopDropdownItem
              key={`local:${branch}`}
              onSelect={() => onChange({ type: 'branch', branch, remote: false })}
            >
              <GitBranch className="size-4" />
              <span className="min-w-0 flex-1 truncate">{branch}</span>
              {selected(branch, false) && <Check className="size-4" />}
            </DesktopDropdownItem>
          ))}
          {branches.length === 0 && (
            <p className="px-3 py-4 text-center text-muted-foreground text-xs">No branches found</p>
          )}
          {remoteBranches.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DesktopDropdownLabel>Remote branches</DesktopDropdownLabel>
              {remoteBranches.map((branch) => (
                <DesktopDropdownItem
                  key={`remote:${branch}`}
                  onSelect={() => onChange({ type: 'branch', branch, remote: true })}
                >
                  <GitBranch className="size-4" />
                  <span className="min-w-0 flex-1 truncate">origin/{branch}</span>
                  {selected(branch, true) && <Check className="size-4" />}
                </DesktopDropdownItem>
              ))}
            </>
          )}
        </div>
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

/**
 * Switches the main checkout to another branch — local or remote — or opens the
 * create-branch dialog. Reuses the shared {@link BranchPicker} so the worktree
 * switcher gets the same local | remote toggle as the cloud-handoff picker.
 * Selecting a remote branch checks out the remote's version: it is fetched,
 * and the local branch is created on it or moved to it (asking first when the
 * local branch has commits of its own). Only rendered in local mode before a
 * conversation starts.
 */
export function LocalBranchMenu({
  disabled,
  gitStatus,
  onCreate,
  onOpen,
  onSwitch,
  pending,
  variant = 'inline',
}: {
  disabled: boolean;
  gitStatus: NativeGitStatus;
  onCreate: () => void;
  /** Refreshes the remote branches when the menu opens. */
  onOpen?: () => void;
  onSwitch: (target: BranchTarget) => void;
  pending: boolean;
  variant?: SetupControlVariant;
}) {
  const [scope, setScope] = useState<BranchScope>('local');
  const currentBranch = gitStatus.branch || 'Detached HEAD';

  return (
    <BranchPicker
      currentBranch={gitStatus.branch}
      defaultBranch={gitStatus.defaultBranch}
      footer={
        <>
          <DropdownMenuSeparator />
          <DesktopDropdownItem onSelect={() => onCreate()}>
            <Plus className="size-4" />
            Create branch…
          </DesktopDropdownItem>
        </>
      }
      localBranches={gitStatus.branches}
      onOpen={onOpen}
      onScopeChange={setScope}
      onSelectBranch={(selectedScope, branch) => {
        // Picking the current branch on the remote side brings it up to the remote's version.
        if (selectedScope === 'remote') onSwitch({ branch, remote: true });
        else if (branch !== gitStatus.branch) onSwitch({ branch, remote: false });
      }}
      remoteBranches={gitStatus.remoteBranches}
      scope={scope}
      selectedValue={scope === 'local' ? `local:${gitStatus.branch}` : ''}
      trigger={
        variant === 'inline' ? (
          <Button
            className="h-control-compact max-w-44 gap-1.5 px-2 text-xs"
            disabled={disabled}
            title="Switch the checkout to a local or remote branch"
            variant="ghost"
          >
            {pending ? (
              <LoaderCircle className="size-3.5 shrink-0 animate-spin" />
            ) : (
              <GitBranch className="size-3.5 shrink-0" />
            )}
            <span className="truncate">{currentBranch}</span>
            <ChevronDown className="size-3 shrink-0" />
          </Button>
        ) : (
          <SetupBranchTrigger
            disabled={disabled}
            label={currentBranch}
            pending={pending}
            title="Switch the checkout to a local or remote branch"
            variant={variant}
          />
        )
      }
    />
  );
}
